import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store, Copy, Share2, ExternalLink, Check, Twitter, Link as LinkIcon,
} from 'lucide-react';
import CreatorLayout from './CreatorLayout';
import VerifiedBadge from '../../components/shared/VerifiedBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

const CreatorStore = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    fetch(`${API_URL}/api/creator/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setProfile(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const userId = (() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || '{}').id; } catch { return null; }
  })();

  const storeUrl = userId
    ? `${window.location.origin}/marketplace/creators/${userId}`
    : null;

  const handleCopy = () => {
    if (!storeUrl) return;
    navigator.clipboard.writeText(storeUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    if (navigator.share && storeUrl) {
      navigator.share({ title: 'Check out my store on IVisora', url: storeUrl });
    } else {
      handleCopy();
    }
  };

  const handleViewStore = () => {
    if (userId) navigate(`/marketplace/creators/${userId}`);
  };

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">My Store</h1>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Store URL card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
                <Store className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 inline-flex items-center gap-1">
                  {profile?.display_name || 'Your Store'}
                  <VerifiedBadge verified={profile?.verification?.status === 'verified'} />
                </p>
                <p className="text-sm text-gray-500">{profile?.bio || 'No bio yet'}</p>
              </div>
            </div>

            {/* Store URL */}
            {storeUrl && (
              <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3 mb-5">
                <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <p className="text-sm text-gray-600 truncate flex-1">{storeUrl}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleViewStore}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View public store
              </button>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                  copied
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share store
              </button>
            </div>
          </div>

          {/* Share on social */}
          {storeUrl && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <p className="font-semibold text-gray-900 mb-4">Share on social media</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=Check out my design templates on IVisora!&url=${encodeURIComponent(storeUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-50 text-sky-600 text-sm font-semibold hover:bg-sky-100 transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                  Share on Twitter
                </a>
              </div>
            </div>
          )}

          {/* Quick stats teaser */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <p className="font-semibold text-gray-900 mb-1">Promote your store</p>
            <p className="text-sm text-gray-500">
              Share your store link with your audience to drive more sales. Buyers can browse all your published templates, follow your store, and get notified when you publish new work.
            </p>
          </div>
        </div>
      )}
    </CreatorLayout>
  );
};

export default CreatorStore;
